import { IconFromSet } from "@gettactic/components";
import { getCssVariableValue } from "@gettactic/helpers/src/css/getCssVariableValue";
import { getScheduleUser } from "@gettactic/helpers/src/schedules/getScheduleUser";
import React from "react";
import ReactDOMServer from "react-dom/server";
import { MapInteractiveZone, SpotShape } from "../../../../types";
import { computeAutomaticStateIconCenterOffset } from "../../../../utils/stateIconCentering";
import { toDataUri } from "../../../../utils/svgAssetCache";
import { useMapCanvas } from "../../contexts/MapCanvasContext";
import {
  DEFAULT_NODE_CIRCLE_RADIUS,
  DEFAULT_NODE_CIRCLE_STROKE_WIDTH,
  DEFAULT_NODE_RECT_COLOR,
  DEFAULT_STATE_ICON_COLOR
} from "../../defines";
import { SpotLayout } from "../../types";
import { CircleProfilePhotoSVG } from "./CircleProfilePhotoSVG";
import { getInteractiveZoneStyles } from "./helpers";

export interface InteractiveZoneSVGProps {
  shape: SpotShape;
  layout: SpotLayout;
  interactiveZone?: MapInteractiveZone;
  selectIndex?: number;
  isActive?: boolean;
  isSpotHovered?: boolean;
}

export const InteractiveZoneSVG: React.FC<InteractiveZoneSVGProps> = ({
  shape,
  layout,
  interactiveZone,
  selectIndex,
  isActive,
  isSpotHovered
}) => {
  const { isPrivacyMode, authenticatedUser, isViewMode, resourceColors } =
    useMapCanvas();
  // A host colour (heatmap) paints the dot too, so the spot reads as one
  // block of colour instead of an availability dot on a tinted body.
  const hostColor = shape.resource_id
    ? resourceColors?.[shape.resource_id]
    : undefined;
  const assignedUser = interactiveZone?.assignedUser || null;
  const actualSchedule = interactiveZone?.actualSchedule || null;
  const scheduleUser = getScheduleUser(actualSchedule);
  // A walk-up check-in with no real person attached is stamped with a
  // "walkup" placeholder identity — nothing to show an avatar for, the
  // plain occupied dot already says enough.
  const isWalkUpPlaceholderUser = !!scheduleUser?.roles?.includes("walkup");

  const circleStyle = getInteractiveZoneStyles({
    isSelected: interactiveZone?.isSelected || false,
    isLastAccessed: interactiveZone?.isLastUsed || false,
    isOccupied: interactiveZone?.isOccupied || false,
    notAssignedResource: interactiveZone?.notAssigned || false,
    notAssignedUser: !assignedUser,
    requiresApproval: interactiveZone?.requiresApproval || false,
    isWalkUp: interactiveZone?.isWalkUp || false,
    hasSchedules: interactiveZone?.hasSchedules || false,
    // showCircleProfilePhoto normally makes the dot transparent so the
    // avatar rendered below shows through it — but for the walk-up
    // placeholder we suppress that avatar, so the dot must stay opaque
    // (its own occupied color) instead of going invisible.
    showCircleProfilePhoto:
      (interactiveZone?.showCircleProfilePhoto || false) &&
      !isWalkUpPlaceholderUser,
    hasCheckedIn: interactiveZone?.actualSchedule?.has_checked_in || false
  });

  const isSelected = interactiveZone?.isSelected || false;
  // The selection recolors the dot's ring. Applied here (not only via
  // getInteractiveZoneStyles) because booked/occupied states return early from
  // that helper before its `isSelected` branch, leaving a white ring on a
  // selected occupied spot. Mirrors the legacy map's `!important` selected rule.
  const selectedStroke =
    getCssVariableValue("--map-spot-selected-stroke") || "#4F48F7";
  const circleStrokeWidth =
    shape.circleProps?.strokeWidth || DEFAULT_NODE_CIRCLE_STROKE_WIDTH;
  const circleRadius = shape.circleProps?.radius || DEFAULT_NODE_CIRCLE_RADIUS;
  const pulseRadius = circleRadius * 1.8;
  const gradientId = `pulse-grad-${shape.id}`;
  const selectionPosition = interactiveZone?.selectionPosition;
  const badgeRadius = circleRadius * 0.75;

  const circleColor =
    selectIndex !== undefined
      ? "#2563eb"
      : isSpotHovered
        ? "#828383"
        : (hostColor ?? circleStyle.fill);

  // The dot (and anything that stands in for it — a profile photo or a
  // state icon) disappears when the spot's highlight area is configured as
  // the sole indicator ("Show Dot" off). It stays in the DOM invisibly
  // either way — the pulse layer and zoom anchors locate spots through it.
  // Zone-less renders (e.g. dashboard mini maps) keep the dot: they don't
  // render the state area, so hiding it would leave nothing at all.
  const isHideDotConfigured =
    isViewMode && !!interactiveZone && shape.stateArea?.hideDot === true;

  // Availability-state icon replacing the dot (e.g. a car when a parking
  // spot is occupied). Profile photos win over icons when the dot itself is
  // shown; when it's hidden, the area is the sole indicator and neither the
  // plain dot nor a photo should appear — the state icon takes over
  // instead. Editor modes (selectIndex / !isViewMode) never show either.
  const showsProfilePhoto =
    !isHideDotConfigured &&
    (!!assignedUser || !!interactiveZone?.showCircleProfilePhoto);
  const stateIcon =
    isViewMode &&
    isActive &&
    selectIndex === undefined &&
    interactiveZone &&
    !showsProfilePhoto
      ? interactiveZone.isOccupied
        ? shape.stateIcons?.occupied
        : shape.stateIcons?.available
      : undefined;
  // The dot's own fill isn't a good icon color: some availability states
  // (e.g. occupied/unavailable) deliberately pair a near-white fill with a
  // colored stroke for contrast — a ring the icon doesn't have, so mirroring
  // the fill would make the icon nearly invisible. A configured
  // `stateIcons.color` wins; otherwise fall back to one fixed, always-legible
  // neutral instead of trying to derive one from the dot's styling.
  const stateIconColor = shape.stateIcons?.color || DEFAULT_STATE_ICON_COLOR;
  // Defaults preserve the legacy look (centered on the dot, sized to match
  // it) for spots that don't configure a position/size. When neither offset
  // has ever been customized AND a highlight area is configured, default to
  // centering on that area instead of the dot — the dot's own layout
  // position (e.g. a desk's) is often nowhere near the room/area's visual
  // center, which otherwise makes a freshly-configured icon look
  // "off-center" until someone manually hits "Center on Area".
  const hasCustomIconOffset =
    shape.stateIcons?.offsetX !== undefined ||
    shape.stateIcons?.offsetY !== undefined;
  const autoCenterOffset = !hasCustomIconOffset
    ? computeAutomaticStateIconCenterOffset(shape)
    : null;
  // A spot type with no rect of its own (Node) has nothing that naturally
  // scales/centers with a configured highlight area — desk/room/parking all
  // size and center their own rect (and therefore their dot) around
  // `nodeWidth`/`nodeHeight` already, but Node's dot sits at a fixed anchor
  // regardless of the spot's configured size. Center the dot itself on the
  // highlight area in that case (e.g. a grid tool cell), the same way the
  // state icon already does, so a plain dot inside a custom-sized area
  // doesn't render pinned in a corner.
  const hasOwnRect = !!(layout.rect.width && layout.rect.height);
  const dotCenterOffset = !hasOwnRect
    ? computeAutomaticStateIconCenterOffset(shape)
    : null;
  const dotOffsetX = dotCenterOffset?.offsetX ?? 0;
  const dotOffsetY = dotCenterOffset?.offsetY ?? 0;
  const dotLayout: SpotLayout =
    dotOffsetX || dotOffsetY
      ? {
          ...layout,
          circle: {
            x: layout.circle.x + dotOffsetX,
            y: layout.circle.y + dotOffsetY
          }
        }
      : layout;
  // Parking rects are sized to the spot's own configured width/height (grid
  // tool cells can be much smaller than the desk-sized default), so neither
  // the legacy circleRadius-based default nor a stale configured size can be
  // trusted to fit — clamp the rendered size to the rect's bounds for
  // parking specifically, rather than changing the long-standing default
  // other spot types already rely on. This is about the spot's own grid-cell
  // sizing, not how it's currently rendered — a Default-appearance parking
  // spot still has a (possibly tiny) parking-sized cell, so this keys off
  // the spot's actual type, not its effective appearance.
  const isParking =
    shape.spotType === "parking_lot" || shape.spotType === "parking_space";
  // Prefer the spot's own configured highlight-area rect (what the grid
  // tool actually paints as the cell — a `kind: "rect"` state area, sized
  // independently of `shape.width`/`shape.height`) over `layout.rect` for
  // this bound. `layout.rect` is derived from `shape.width`/`shape.height`,
  // which can end up out of sync with the highlight area it was created
  // alongside (e.g. across a save/reload round-trip) — clamping against the
  // rect that's actually painted on screen can't overflow it regardless.
  const parkingBoundsWidth =
    shape.stateArea?.kind === "rect" && shape.stateArea.rect?.width
      ? shape.stateArea.rect.width
      : layout.rect.width;
  const parkingBoundsHeight =
    shape.stateArea?.kind === "rect" && shape.stateArea.rect?.height
      ? shape.stateArea.rect.height
      : layout.rect.height;
  // Clamping to the exact rect size still reads as "too big" — the glyph's
  // own bounding box ends up flush with the rect's edges on its shorter
  // side, touching the boundary with zero margin. Leave a visible gap on
  // all sides instead of letting the icon fill the cell edge-to-edge.
  const PARKING_ICON_FIT_RATIO = 0.7;
  const configuredIconSize = shape.stateIcons?.size || circleRadius * 2;
  const stateIconSize =
    isParking && parkingBoundsWidth && parkingBoundsHeight
      ? Math.min(
          configuredIconSize,
          parkingBoundsWidth * PARKING_ICON_FIT_RATIO,
          parkingBoundsHeight * PARKING_ICON_FIT_RATIO
        )
      : configuredIconSize;
  // When the dot itself already auto-centered on the highlight area above
  // (no own rect), the icon sitting at the dot's position is already
  // centered too — adding the auto-center delta again on top would double
  // it. Types that keep their own natural rect/position (desk/room/parking)
  // are unaffected: the dot didn't move, so the icon still auto-centers
  // exactly as before.
  const stateIconOffsetX =
    shape.stateIcons?.offsetX ??
    (hasOwnRect ? (autoCenterOffset?.offsetX ?? 0) : 0);
  const stateIconOffsetY =
    shape.stateIcons?.offsetY ??
    (hasOwnRect ? (autoCenterOffset?.offsetY ?? 0) : 0);

  // Rendered as an `<image>` (a rasterized data URI) rather than inlining
  // `IconFromSet`'s own nested `<svg>` directly. A nested `<svg>`'s CSS
  // `style.width`/`style.height` is silently not applied to its own
  // rendered geometry by browsers — only its plain `width`/`height`
  // attributes are (each icon file, e.g. a bike icon, hardcodes its own
  // native pixel size as those attributes) — so inlining it ignored our
  // `stateIconSize` entirely and rendered at that native size instead,
  // which is bigger than a parking cell. `<image>` doesn't have that
  // quirk: its `width`/`height` attributes reliably control its footprint
  // and scale correctly with the map's own zoom/pan transform, the same
  // way the rect they must fit inside does.
  const iconMarkup = React.useMemo(() => {
    if (!stateIcon) return null;
    // IconFromSet's own `color` prop only reaches the icon's outer wrapper
    // — mdi/lucide paint the glyph directly, but tactic icons paint it with
    // a literal `currentColor` that relies on CSS inheritance from a live
    // ancestor to resolve. That inheritance doesn't exist once this markup
    // is detached into a standalone data URI, so resolve it here instead
    // (mirrors the Konva editor preview's `loadColoredSvgMarkupImage`).
    return ReactDOMServer.renderToStaticMarkup(
      <IconFromSet icon={stateIcon} preserveAspectRatio="xMidYMid meet" />
    ).replace(/currentColor/g, stateIconColor);
  }, [stateIcon, stateIconColor]);
  const iconDataUri = React.useMemo(
    () => (iconMarkup ? toDataUri(iconMarkup) : null),
    [iconMarkup]
  );

  const isDotHidden = !!stateIcon || isHideDotConfigured;
  // Once an icon replaces the dot, its fill is normally fully transparent —
  // but a configured `occupiedBackgroundColor` gives it a colored backdrop
  // (e.g. a red disc behind a car icon) specifically while occupied/booked.
  const hasOccupiedIconBackground =
    !!stateIcon &&
    !!interactiveZone?.isOccupied &&
    !!shape.stateIcons?.occupiedBackgroundColor;

  // In view mode the group carries the same `interactive-{resource_id}` id
  // the clone overlay used to provide — zoom-to-resource, the hover-details
  // popper and the pulse layer all anchor to it via the DOM.
  const anchorId =
    isViewMode && shape.resource_id
      ? `interactive-${shape.resource_id}`
      : undefined;

  return (
    <g opacity={circleStyle.fillOpacity} id={anchorId}>
      <g id={shape.resource_id}>
        {/* No dot pulse for area-only spots — the state area itself pulses
          (SpotShapeItemSVG); a ring around an invisible dot reads wrong. */}
        {isSelected && !isHideDotConfigured && (
          <>
            <defs>
              <radialGradient id={gradientId} cx="50%" cy="50%" r="50%">
                <stop offset="0.6" stopColor="#656CFF" stopOpacity="0.4" />
                <stop offset="1" stopColor="#656CFF" stopOpacity="0" />
              </radialGradient>
            </defs>
            <circle
              cx={dotLayout.circle.x}
              cy={dotLayout.circle.y}
              r={pulseRadius}
              fill={`url(#${gradientId})`}
            >
              <animate
                attributeName="r"
                values={`${pulseRadius * 0.85};${pulseRadius};${pulseRadius * 0.85}`}
                dur="2s"
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                values="0.7;1;0.7"
                dur="2s"
                repeatCount="indefinite"
              />
            </circle>
            <circle
              cx={dotLayout.circle.x}
              cy={dotLayout.circle.y}
              r={circleRadius + 3}
              fill="none"
              stroke="#4F48F7"
              strokeWidth={3}
            >
              <animate
                attributeName="opacity"
                values="0.6;1;0.6"
                dur="2s"
                repeatCount="indefinite"
              />
            </circle>
          </>
        )}
        <circle
          id={`shape-${shape.id}-circle`}
          // Tells the pulse layer to brighten the state area instead of
          // showing a pulse circle — the dot is hidden (icon or area-only),
          // so a floating pulse ring would hover over nothing.
          data-dot-hidden={isHideDotConfigured ? "true" : undefined}
          // When this circle is standing in as the occupied icon's backdrop
          // (not the plain dot), it needs to move/resize with the icon —
          // the icon can be offset or auto-centered away from the dot's own
          // position, and a backdrop left behind at the old dot center would
          // no longer sit behind it.
          cx={
            hasOccupiedIconBackground
              ? dotLayout.circle.x + stateIconOffsetX
              : dotLayout.circle.x
          }
          cy={
            hasOccupiedIconBackground
              ? dotLayout.circle.y + stateIconOffsetY
              : dotLayout.circle.y
          }
          r={hasOccupiedIconBackground ? stateIconSize / 2 : circleRadius}
          fill={
            isDotHidden
              ? hasOccupiedIconBackground
                ? shape.stateIcons?.occupiedBackgroundColor
                : "transparent"
              : isActive
                ? circleColor
                : shape.isInactive
                  ? shape.rectProps?.color || DEFAULT_NODE_RECT_COLOR
                  : "grey"
          }
          stroke={
            isDotHidden
              ? "none"
              : isSelected
                ? selectedStroke
                : (hostColor ?? circleStyle.stroke)
          }
          strokeWidth={isDotHidden ? 0 : circleStrokeWidth}
          strokeDasharray={
            isDotHidden ? undefined : circleStyle.dash?.join(" ")
          }
        />
        {stateIcon && iconDataUri ? (
          <image
            href={iconDataUri}
            x={dotLayout.circle.x - stateIconSize / 2 + stateIconOffsetX}
            y={dotLayout.circle.y - stateIconSize / 2 + stateIconOffsetY}
            width={stateIconSize}
            height={stateIconSize}
            preserveAspectRatio="xMidYMid meet"
          />
        ) : null}
        {assignedUser && !isHideDotConfigured ? (
          <CircleProfilePhotoSVG
            privacy={
              isPrivacyMode && assignedUser.id !== authenticatedUser?.user?.id
            }
            profilePhoto={
              interactiveZone?.assignedUser?.profile_photo_url || ""
            }
            radius={circleRadius - circleStrokeWidth / 2}
            layout={dotLayout}
            name={assignedUser.name || ""}
            // Dedicated resources show their person at full strength —
            // the faded look is only for regular restricted assignments.
            restricted={!interactiveZone?.isDedicated}
            isSelected={isSelected}
          />
        ) : null}
        {interactiveZone?.showCircleProfilePhoto &&
        !isHideDotConfigured &&
        !isWalkUpPlaceholderUser ? (
          <CircleProfilePhotoSVG
            privacy={
              isPrivacyMode && scheduleUser?.id !== authenticatedUser?.user?.id
            }
            profilePhoto={scheduleUser?.profile_photo_url ?? ""}
            radius={circleRadius - circleStrokeWidth / 2}
            layout={dotLayout}
            name={scheduleUser?.name || ""}
            hasCheckedIn={actualSchedule?.has_checked_in || false}
            grayscale={scheduleUser?.roles?.includes("deleted")}
            isSelected={isSelected}
          />
        ) : null}
        {selectionPosition ? (
          <g>
            <circle
              cx={dotLayout.circle.x + circleRadius}
              cy={dotLayout.circle.y - circleRadius}
              r={badgeRadius}
              fill={selectedStroke}
              stroke="#ffffff"
              strokeWidth={2}
            />
            <text
              x={dotLayout.circle.x + circleRadius}
              y={dotLayout.circle.y - circleRadius}
              textAnchor="middle"
              dominantBaseline="central"
              fill="#ffffff"
              fontSize={badgeRadius * 1.2}
              fontWeight={700}
            >
              {selectionPosition}
            </text>
          </g>
        ) : null}
      </g>
    </g>
  );
};
